import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIAdmin } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const addCargoType = createAsyncThunk(
  'cargoType/add',
  async (cargoTypeData, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminAddCargoType, {}, 'POST', cargoTypeData);
      if (response?.data?.status) {
        return response.data.data;
      }
      return rejectWithValue(response?.data?.message || 'Failed to add cargo type');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateCargoType = createAsyncThunk(
  'cargoType/update',
  async ({ id, cargoTypeData }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminUpdateCargoType, {}, 'PATCH', { _id: id, ...cargoTypeData });
      if (response?.data?.status) {
        return response.data.data;
      }
      return rejectWithValue(response?.data?.message || 'Failed to update cargo type');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const deleteCargoType = createAsyncThunk(
  'cargoType/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminDeleteCargoType, { _id: id }, 'DELETE', {});
      if (response?.data?.status) {
        return { id, message: response.data.message };
      }
      return rejectWithValue(response?.data?.message || 'Failed to delete cargo type');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateCargoTypeStatus = createAsyncThunk(
  'cargoType/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminUpdateStatusCargoType, { _id: id, status }, 'PATCH', {});
      if (response?.data?.status) {
        return { id, status, message: response.data.message };
      }
      return rejectWithValue(response?.data?.message || 'Failed to update status');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

const initialState = {
  mutationLoading: false,
  mutationError: null,
  mutationSuccess: null,
};

const adminCargoTypeSlice = createSlice({
  name: 'cargoType',
  initialState,
  reducers: {
    clearMutationError: (state) => {
      state.mutationError = null;
    },
    clearMutationSuccess: (state) => {
      state.mutationSuccess = null;
    },
    resetMutationState: (state) => {
      state.mutationLoading = false;
      state.mutationError = null;
      state.mutationSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addCargoType.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(addCargoType.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Cargo type added successfully';
      })
      .addCase(addCargoType.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateCargoType.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateCargoType.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Cargo type updated successfully';
      })
      .addCase(updateCargoType.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(deleteCargoType.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(deleteCargoType.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Cargo type deleted successfully';
      })
      .addCase(deleteCargoType.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateCargoTypeStatus.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateCargoTypeStatus.fulfilled, (state, action) => {
        state.mutationLoading = false;
        state.mutationSuccess = `Status changed to ${action.payload?.status}`;
      })
      .addCase(updateCargoTypeStatus.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      });
  },
});

export const { clearMutationError, clearMutationSuccess, resetMutationState } = adminCargoTypeSlice.actions;
export default adminCargoTypeSlice.reducer;